import { useEffect, useState } from "react";
import { useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { apiRequest } from "@/lib/queryClient";
import { formatDistanceToNow } from "date-fns";
import { sk } from "date-fns/locale";
import { CircleAlert, Loader2 } from "lucide-react";

interface SharedTaskData {
  task: {
    id: number;
    title: string;
    description: string;
    status: "pending" | "in_progress" | "completed";
    dueDate: string | null;
    createdAt: string;
  };
  client: {
    name: string;
  };
}

export default function SharedTask() {
  const [location] = useLocation();
  const [taskData, setTaskData] = useState<SharedTaskData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchTask = async () => {
      try {
        // Rozdelíme URL a extrahujeme ID úlohy a token
        const path = location.split("?")[0]; // Získame časť pred otáznikom
        const taskId = path.split("/").pop(); // Získame ID z URL
        const params = new URLSearchParams(location.includes('?') ? location.split('?')[1] : '');
        const token = params.get('token'); // Získame token z URL parametrov
        
        if (!taskId || isNaN(parseInt(taskId))) {
          setError("Neplatné ID úlohy");
          setIsLoading(false);
          return;
        }

        if (!token) {
          setError("Chýbajúci bezpečnostný token");
          setIsLoading(false);
          return;
        }

        const response = await apiRequest("GET", `/api/shared-task/${taskId}?token=${token}`);
        const data = await response.json();

        if (data) {
          setTaskData(data);
        } else {
          setError("Úloha nebola nájdená alebo nie je verejne zdieľaná");
        }
      } catch (err) {
        console.error("Error loading shared task:", err);
        setError("Chyba pri načítaní úlohy");
      } finally {
        setIsLoading(false);
      }
    };

    fetchTask();
  }, [location]);

  const getStatusLabel = (status: string) => {
    switch (status) {
      case "pending":
        return "Čaká na spracovanie";
      case "in_progress":
        return "Rozpracované";
      case "completed":
        return "Dokončené";
      default:
        return status;
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error || !taskData) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-4">
        <CircleAlert className="h-12 w-12 text-destructive mb-4" />
        <h2 className="text-2xl font-bold mb-2">Obsah nie je dostupný</h2>
        <p className="text-center text-muted-foreground mb-6">{error || "Úloha sa nenašla"}</p>
        <Button variant="outline" onClick={() => window.history.back()}>Späť</Button>
      </div>
    );
  }

  const { task, client } = taskData;
  const createdDate = new Date(task.createdAt);
  const dueDate = task.dueDate ? new Date(task.dueDate) : null;

  // Helper function to get status badge color
  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case "completed":
        return "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400 border-green-200 dark:border-green-800";
      case "in_progress":
        return "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400 border-blue-200 dark:border-blue-800";
      default:
        return "bg-slate-100 text-slate-800 dark:bg-slate-800 dark:text-slate-300 border-slate-200 dark:border-slate-700";
    }
  };
  
  // Helper function to get due date styling
  const getDueDateClass = () => {
    if (!dueDate) return "";
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const dueDateCopy = new Date(dueDate);
    dueDateCopy.setHours(0, 0, 0, 0);
    
    const diffTime = dueDateCopy.getTime() - today.getTime();
    const diffDays = diffTime / (1000 * 3600 * 24);
    
    if (diffDays < 0) return "text-red-600 dark:text-red-400 font-medium";
    if (diffDays === 0) return "text-orange-600 dark:text-orange-400 font-medium";
    if (diffDays <= 3) return "text-yellow-600 dark:text-yellow-400";
    return "";
  };

  return (
    <div className="min-h-screen flex flex-col">
      {/* Header */}
      <header className="w-full py-4 px-4 sm:px-6 border-b bg-background">
        <div className="flex items-center justify-between w-full">
          <h1 className="text-lg font-semibold">Task Pro</h1>
          <div className="text-sm text-muted-foreground">Zdieľaná úloha #{task.id}</div>
        </div>
      </header>
      
      {/* Main content */}
      <main className="flex-1 py-8 px-4 sm:px-6 bg-muted/30">
        <div className="w-full">
          <div className="mb-6">
            <div className="flex flex-col md:flex-row md:items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold tracking-tight">{task.title}</h1>
                <p className="text-muted-foreground mt-1">Pre klienta: {client.name}</p>
              </div>
              <Badge
                className={`${getStatusBadgeVariant(task.status)} mt-2 md:mt-0 px-3 py-1 text-sm whitespace-nowrap`}
              >
                {getStatusLabel(task.status)}
              </Badge>
            </div>
          </div>
          
          <Card className="overflow-hidden">
            <CardHeader className="pb-4 relative border-b">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <CardTitle>Detaily úlohy</CardTitle>
                  <CardDescription>ID: #{task.id}</CardDescription>
                </div>
              </div>
            </CardHeader>
            
            <CardContent className="pt-6 px-6">
              <div className="grid gap-6 md:grid-cols-2">
                <div>
                  <div className="text-sm font-medium text-muted-foreground mb-1">Vytvorené</div>
                  <div>{createdDate.toLocaleDateString()} ({formatDistanceToNow(createdDate, { addSuffix: true, locale: sk })})</div>
                </div>
                
                {dueDate && (
                  <div>
                    <div className="text-sm font-medium text-muted-foreground mb-1">Termín</div>
                    <div className={getDueDateClass()}>
                      {dueDate.toLocaleDateString()} 
                      <span className="text-muted-foreground ml-1 text-sm">
                        ({formatDistanceToNow(dueDate, { addSuffix: true, locale: sk })})
                      </span>
                    </div>
                  </div>
                )}
              </div>
              
              <Separator className="my-6" />
              
              <div>
                <div className="text-sm font-medium text-muted-foreground mb-2">Popis úlohy</div>
                <div className="whitespace-pre-line p-4 bg-muted/50 rounded-md border">
                  {task.description || "Bez popisu"}
                </div>
              </div>
            </CardContent>
            
            <CardFooter className="flex justify-between py-4 bg-muted/20 border-t px-6">
              <div className="flex items-center text-sm text-muted-foreground">
                <div className="w-3 h-3 rounded-full bg-primary/60 mr-2"></div>
                Aktuálny stav: {getStatusLabel(task.status)}
              </div>
              <div className="text-sm">
                {task.status !== "completed" ? (
                  <span className="text-muted-foreground">Úloha je rozpracovaná</span>
                ) : (
                  <span className="text-green-600 dark:text-green-400">Úloha bola dokončená</span>
                )}
              </div>
            </CardFooter>
          </Card>
        </div>
      </main>
      
      {/* Footer */}
      <footer className="py-6 px-4 sm:px-6 border-t bg-background">
        <div className="w-full text-center text-sm text-muted-foreground">
          <p>© {new Date().getFullYear()} Task Pro — Nástroj pre správu projektov a úloh</p>
        </div>
      </footer>
    </div>
  );
}